import { Link } from "react-router-dom";
import "@/App.css";
import { 
  ShieldAlert, 
  MapPin,
  Phone,
  DollarSign,
  CheckCircle,
  ChevronRight,
  Briefcase,
  Clock,
  Globe,
  ArrowLeft,
  FileText,
  Users,
  Calendar
} from "lucide-react";

function JobGuatemala() {
  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/90 backdrop-blur-sm border-b border-white/10">
        <div className="section-container flex items-center justify-between py-4">
          <Link to="/" className="flex items-center gap-3">
            <ShieldAlert className="w-8 h-8 text-orange-500" strokeWidth={1.5} />
            <span 
              className="font-bold text-xl tracking-tight uppercase"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              data-testid="logo-text"
            >
              Simon Advanced Security
            </span>
          </Link>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="footer-link hover:text-orange-500" data-testid="nav-home">
              Home
            </Link>
            <Link to="/jobs" className="footer-link hover:text-orange-500" data-testid="nav-jobs">
              All Jobs
            </Link>
            <a 
              href="tel:+18008241906" 
              className="btn-primary text-sm py-2 px-4"
              data-testid="nav-call-btn"
            >
              Call Now
            </a>
          </div>
        </div>
      </nav>

      {/* Back Link */}
      <div className="pt-24 pb-4 bg-[#0A0A0A]">
        <div className="section-container">
          <Link 
            to="/jobs" 
            className="inline-flex items-center gap-2 text-orange-500 hover:text-orange-400 transition-colors text-sm"
            data-testid="back-to-jobs"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to All Jobs
          </Link>
        </div>
      </div>

      {/* Job Header */}
      <section className="py-8 bg-[#0A0A0A] relative overflow-hidden" data-testid="job-header">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 50px, rgba(249,115,22,0.1) 50px, rgba(249,115,22,0.1) 51px),
                              repeating-linear-gradient(90deg, transparent, transparent 50px, rgba(249,115,22,0.1) 50px, rgba(249,115,22,0.1) 51px)`
          }} />
        </div>
        
        <div className="section-container relative z-10">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-4">
              <span className="bg-orange-500/20 border border-orange-500/50 text-orange-400 text-xs px-3 py-1 uppercase tracking-wider">
                Covert Operations
              </span>
              <span className="bg-blue-500/20 border border-blue-500/50 text-blue-400 text-xs px-3 py-1 uppercase tracking-wider">
                U.S. Personnel Only
              </span>
            </div>
            
            <h1 
              className="text-3xl md:text-5xl lg:text-6xl font-black tracking-tighter uppercase text-white mb-6"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              data-testid="job-title"
            >
              Executive Protection Team – Guatemala
            </h1>

            <div className="flex flex-wrap gap-4 mb-8">
              <div className="flex items-center gap-2 text-gray-400">
                <MapPin className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
                <span className="text-sm">Guatemala</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <DollarSign className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
                <span className="text-sm">$600/day + Expenses</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <Calendar className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
                <span className="text-sm">30 Consecutive Days</span>
              </div>
            </div>

            <a 
              href="tel:+18008241906"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="apply-btn-top"
            >
              <Phone className="w-5 h-5" />
              Apply Now: 800-824-1906
            </a>
          </div>
        </div>
      </section>

      {/* Job Content */}
      <section className="py-16 bg-[#121212]">
        <div className="section-container">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-12">
              {/* Overview */}
              <div data-testid="job-overview">
                <h2 
                  className="text-2xl md:text-3xl font-bold tracking-tight uppercase text-white mb-6"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  Position Overview
                </h2>
                <div className="prose prose-invert max-w-none">
                  <p className="text-gray-400 text-base leading-relaxed mb-4">
                    A private U.S. client requires an experienced <strong className="text-white">Executive Protection Team</strong> to support a 30-day covert protection assignment in <strong className="text-white">Guatemala</strong>. This role requires a low-profile protective posture supporting a U.S. principal during extended travel within the country.
                  </p>
                  <p className="text-gray-400 text-base leading-relaxed mb-4">
                    The assignment involves the ability to operate independently while maintaining a professional and culturally appropriate presence. Additional work with this client may continue after this assignment for exceptional candidates.
                  </p>
                  <div className="bg-orange-500/10 border border-orange-500/30 p-4 my-6">
                    <div className="flex items-start gap-3">
                      <FileText className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                      <p className="text-gray-300 text-sm">
                        <strong className="text-orange-400">Important:</strong> The agents selected for this detail must be comfortable operating with minimal footprint and limited infrastructure, where judgment and experience are the primary assets. While supplemental assets may be staged in-country for emergencies, the assigned agents should plan and operate as if immediate support may not be available.
                      </p>
                    </div>
                  </div>
                  <p className="text-gray-400 text-base leading-relaxed">
                    Due to the nature of the assignment, specific mission details will only be shared with vetted candidates who are selected for a direct interview with the employer.
                  </p>
                </div>
              </div>

              {/* Duties & Responsibilities */}
              <div data-testid="job-duties">
                <h2 
                  className="text-2xl md:text-3xl font-bold tracking-tight uppercase text-white mb-6"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  Duties & Responsibilities
                </h2>
                <ul className="space-y-4">
                  {[
                    "Provide low-profile executive protection for a U.S. principal during in-country travel and daily activities",
                    "Maintain a professional protective presence that balances discretion with readiness",
                    "Conduct advances on all routes and venues where the principal will be traveling",
                    "Maintain secure communications with a coordinating team (U.S. personnel) regarding movement status and operational updates",
                    "Coordinate discreetly with local logistical and security resources when required",
                    "Maintain professional conduct and appropriate cultural awareness within the operating environment",
                    "Ensure the safety and well-being of the principal while maintaining low visibility and minimal disruption to normal activities"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <ChevronRight className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                      <span className="text-gray-300 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Minimum Qualifications */}
              <div data-testid="job-requirements">
                <h2 
                  className="text-2xl md:text-3xl font-bold tracking-tight uppercase text-white mb-6"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  Minimum Qualifications
                </h2>
                <p className="text-red-400 text-sm mb-6 uppercase tracking-wider">
                  Do not apply without meeting ALL minimum qualification requirements
                </p>
                <ul className="space-y-4">
                  {[
                    { text: "Must have prior U.S. military experience or operational security experience in Guatemala (U.S. citizens only)", critical: true },
                    { text: "Prior low-profile executive protection experience", critical: true },
                    { text: "Valid passport and ability to deploy internationally", critical: false },
                    { text: "Experience operating with minimal logistical support or infrastructure", critical: false },
                    { text: "Physically capable of operating in varied environments and terrain", critical: false },
                    { text: "Familiarity with Guatemala or surrounding regional environments", critical: false },
                    { text: "Spanish language proficiency (operational or conversational)", critical: true }
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${item.critical ? 'text-red-500' : 'text-orange-500'}`} strokeWidth={1.5} />
                      <span className={`text-sm ${item.critical ? 'text-white font-medium' : 'text-gray-300'}`}>{item.text}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Preferred Qualifications */}
              <div data-testid="job-preferred">
                <h2 
                  className="text-2xl md:text-3xl font-bold tracking-tight uppercase text-white mb-6"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  Preferred Qualifications
                </h2>
                <p className="text-gray-400 text-sm mb-6">
                  Meeting any of the qualifications from this list will increase your competitiveness in the selection process:
                </p>
                <ul className="space-y-4">
                  {[
                    "Prior U.S. military SOF, particularly former 18-series from 7th SFG or other SOF with experience operating in Guatemala (or other countries in Central or South America)",
                    "Former 18D, 1T2X1 or similar advanced medical backgrounds on covert protection teams",
                    "Prior experience supporting UHNW Principals",
                    "Previous international executive protection or protective operations",
                    "Prior Foreign Internal Defense (FID), advisory, or security cooperation missions",
                    "Fluent/Native level language proficiency in Spanish",
                    "Experience coordinating local logistics or host-nation resources"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-orange-500/60 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                      <span className="text-gray-400 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Required Skills */}
              <div data-testid="job-skills">
                <h2 
                  className="text-2xl md:text-3xl font-bold tracking-tight uppercase text-white mb-6"
                  style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                >
                  Required Skills
                </h2>
                <div className="flex flex-wrap gap-2">
                  {[
                    "Executive Protection",
                    "Logistics",
                    "Medical",
                    "Operations",
                    "PSD - Foreign"
                  ].map((skill, index) => (
                    <span 
                      key={index}
                      className="bg-orange-500/10 border border-orange-500/30 text-orange-400 text-xs px-3 py-2 uppercase tracking-wider"
                    >
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-6">
                {/* Apply Card */}
                <div className="bg-black/40 border border-white/10 p-6" data-testid="apply-card">
                  <h3 
                    className="text-xl font-bold uppercase tracking-tight text-white mb-4"
                    style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
                  >
                    Apply For This Position
                  </h3>
                  <div className="space-y-4 mb-6">
                    <div className="flex items-center gap-3">
                      <DollarSign className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
                      <div>
                        <p className="text-gray-500 text-xs uppercase">Compensation</p>
                        <p className="text-white text-lg font-bold">$600/day + Expenses</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Globe className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
                      <div>
                        <p className="text-gray-500 text-xs uppercase">Location</p>
                        <p className="text-white">Guatemala</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Briefcase className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
                      <div>
                        <p className="text-gray-500 text-xs uppercase">Employment Type</p>
                        <p className="text-white">Full-Time Contract (1099)</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Calendar className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
                      <div>
                        <p className="text-gray-500 text-xs uppercase">Schedule</p>
                        <p className="text-white">30 Consecutive Days</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Clock className="w-5 h-5 text-orange-500" strokeWidth={1.5} />
                      <div>
                        <p className="text-gray-500 text-xs uppercase">Start Date</p>
                        <p className="text-white">Immediate</p>
                      </div>
                    </div>
                  </div>
                  <a 
                    href="tel:+18008241906"
                    className="btn-primary w-full flex items-center justify-center gap-2"
                    data-testid="apply-btn-sidebar"
                  >
                    <Phone className="w-5 h-5" />
                    800-824-1906
                  </a>
                </div>

                {/* U.S. Personnel Notice */}
                <div className="bg-blue-500/10 border border-blue-500/30 p-6">
                  <div className="flex items-start gap-3">
                    <Users className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <div>
                      <h4 className="text-white font-bold text-sm mb-2">U.S. Citizens Only</h4>
                      <p className="text-gray-400 text-xs">
                        This position is restricted to U.S. personnel with valid U.S. passports and ability to deploy internationally.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Other Jobs Link */}
                <Link 
                  to="/jobs"
                  className="block bg-black/40 border border-white/10 p-6 hover:border-orange-500/50 transition-colors"
                  data-testid="other-jobs-link"
                >
                  <h4 className="text-white font-bold text-sm mb-2">View Other Positions</h4>
                  <p className="text-gray-400 text-xs">
                    Explore more high-threat security positions worldwide.
                  </p>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-[#0A0A0A] border-t border-white/10">
        <div className="section-container">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Link to="/" className="flex items-center gap-3">
              <ShieldAlert className="w-6 h-6 text-orange-500" strokeWidth={1.5} />
              <span 
                className="font-bold text-lg tracking-tight uppercase text-white"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                Simon Advanced Security
              </span>
            </Link>
            <p className="text-gray-500 text-xs tracking-widest">
              © {new Date().getFullYear()} ALL RIGHTS RESERVED
            </p>
            <div className="flex items-center gap-6">
              <Link to="/" className="footer-link" data-testid="footer-home">Home</Link>
              <Link to="/jobs" className="footer-link" data-testid="footer-jobs">Careers</Link>
              <a href="tel:+18008241906" className="footer-link" data-testid="footer-call">Call Us</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default JobGuatemala;
